import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useCurrency } from "../context/CurrencyContext";
import { useTranslation } from "react-i18next";
import { Clock, KeyRound, Star } from "lucide-react";
import ReviewForm from "../components/ReviewForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isReviewable(b) {
  if (b.payment_status !== "paid" || b.reviewed) return false;
  try {
    const start = new Date(b.check_in).getTime();
    const end = start + (parseInt(b.duration_hours, 10) || 0) * 3600_000;
    return end <= Date.now();
  } catch (_e) { return false; }
}

export default function MyBookings() {
  const { format } = useCurrency();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewFor, setReviewFor] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/my/bookings`).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const reviewable = useMemo(() => items.filter(isReviewable), [items]);

  return (
    <div className="mx-auto max-w-4xl px-6 py-10 lg:px-8">
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("myBookings.title")}</h1>
      {reviewable.length > 0 && (
        <p className="mt-2 text-sm text-copper" data-testid="reviewable-count">
          {t("myBookings.toReview", { count: reviewable.length })}
        </p>
      )}
      <div className="mt-8 space-y-4">
        {loading ? (
          <div className="rounded-3xl bg-white p-8 text-slate-500">{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl bg-white p-10 text-center">
            <p className="text-slate-500">{t("myBookings.noneYet")}</p>
            <Link to="/search" className="btn-primary mt-4 inline-block text-sm">{t("myBookings.findStay")}</Link>
          </div>
        ) : items.map((b) => (
          <div key={b.id} data-testid={`my-booking-${b.id}`} className="rounded-3xl bg-white p-6 card-shadow">
            <Link to={`/booking/${b.id}`} className="block">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display text-xl font-bold text-slate-900">{b.property_title}</div>
                  <div className="mt-1 flex flex-wrap gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {new Date(b.check_in).toLocaleString()} · {b.duration_hours}h</span>
                    <span className="flex items-center gap-1 capitalize"><KeyRound className="h-4 w-4" /> {t("myBookings.keyType", { type: b.key_type })}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold text-slate-900">{format(b.total_zmw)}</div>
                  <div className={`mt-1 inline-block rounded-full px-2 py-1 text-xs font-semibold ${b.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-800"}`}>
                    {b.payment_status === "paid" ? t("booking.paid") : t("booking.pending")}
                  </div>
                </div>
              </div>
            </Link>
            {isReviewable(b) && (
              <button
                onClick={() => setReviewFor(b)}
                data-testid={`write-review-btn-${b.id}`}
                className="btn-primary mt-4 text-sm"
              >
                <Star className="mr-1 inline h-4 w-4" /> {t("myBookings.writeReview")}
              </button>
            )}
            {b.reviewed && (
              <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-forest">
                <Star className="h-3.5 w-3.5 fill-forest text-forest" /> {t("myBookings.reviewed")}
              </div>
            )}
          </div>
        ))}
      </div>

      <ReviewForm
        booking={reviewFor || {}}
        open={!!reviewFor}
        onOpenChange={(o) => !o && setReviewFor(null)}
        onSubmitted={() => { setReviewFor(null); load(); }}
      />
    </div>
  );
}
